/**
 * Global Search Component
 * Provides search functionality across the entire app
 * Optimized with debouncing and memoization for smooth performance
 * 
 * @author Varun Pandey
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Home, User, FolderPlus, Wallet, Star, BarChart3, TrendingUp, Settings, HelpCircle, Smartphone, Sparkles, Building2, GitCompare, Image, FileText, Target, BookOpen, Award, Calendar, Share2, CalendarCheck, FileCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../utils/cn';
import { faqData } from '../../constants/faqData';

interface SearchResult {
  id: string;
  title: string;
  path: string;
  icon: React.ReactNode;
  category: string;
  keywords: string[];
  type?: 'page' | 'faq';
}

// Search index - all searchable pages and content
const searchIndex: SearchResult[] = [
  // Main Navigation
  {
    id: 'home',
    title: 'Home',
    path: '/user-home',
    icon: <Home className="h-4 w-4" />,
    category: 'Main Navigation',
    keywords: ['home', 'dashboard', 'main', 'overview'],
  },
  {
    id: 'agency-home',
    title: 'Agency Home',
    path: '/agency-home',
    icon: <Building2 className="h-4 w-4" />,
    category: 'Main Navigation',
    keywords: ['agency', 'artists', 'manage', 'team', 'agency dashboard', 'roster'],
  },
  {
    id: 'profile',
    title: 'Profile',
    path: '/profile',
    icon: <User className="h-4 w-4" />,
    category: 'Main Navigation',
    keywords: ['profile', 'account', 'user', 'personal', 'information'],
  },
  {
    id: 'jobs',
    title: 'Jobs',
    path: '/jobs',
    icon: <FolderPlus className="h-4 w-4" />,
    category: 'Main Navigation',
    keywords: ['jobs', 'work', 'opportunities', 'gigs', 'bookings', 'events', 'apply', 'bids', 'express booking', 'fast leads', 'high intent leads'],
  },
  {
    id: 'wallet',
    title: 'Wallet',
    path: '/wallet',
    icon: <Wallet className="h-4 w-4" />,
    category: 'Main Navigation',
    keywords: ['wallet', 'money', 'payment', 'balance', 'earnings', 'transactions', 'finance'],
  },
  {
    id: 'membership',
    title: 'Membership',
    path: '/membership',
    icon: <Star className="h-4 w-4" />,
    category: 'Main Navigation',
    keywords: ['membership', 'plan', 'subscription', 'premium', 'elite', 'economy', 'free'],
  },
  {
    id: 'compare-plans',
    title: 'Compare Plans',
    path: '/compare-plans',
    icon: <GitCompare className="h-4 w-4" />,
    category: 'Main Navigation',
    keywords: ['compare', 'plans', 'pricing', 'quarterly', 'annually', 'free', 'economy', 'premium', 'elite'],
  },
  {
    id: 'statistics',
    title: 'Statistics',
    path: '/statistics',
    icon: <TrendingUp className="h-4 w-4" />,
    category: 'Main Navigation',
    keywords: ['statistics', 'stats', 'analytics', 'data', 'reports', 'insights', 'performance'],
  },
  // Profile sections
  {
    id: 'profile-gallery',
    title: 'Gallery',
    path: '/profile/gallery',
    icon: <Image className="h-4 w-4" />,
    category: 'Profile',
    keywords: ['gallery', 'photos', 'images', 'showcase', 'portfolio', 'media', 'upload'],
  },
  {
    id: 'profile-personal-details',
    title: 'Personal Details',
    path: '/profile/personal-details',
    icon: <FileText className="h-4 w-4" />,
    category: 'Profile',
    keywords: ['personal', 'details', 'verify', 'name', 'email', 'phone', 'information'],
  },
  {
    id: 'profile-performance-details',
    title: 'Performance Details',
    path: '/profile/performance-details',
    icon: <FolderPlus className="h-4 w-4" />,
    category: 'Profile',
    keywords: ['performance', 'pricing', 'rates', 'fee', 'gig', 'event', 'set price'],
  },
  {
    id: 'profile-usp',
    title: 'Unique Sell Point',
    path: '/profile/usp',
    icon: <Target className="h-4 w-4" />,
    category: 'Profile',
    keywords: ['usp', 'unique', 'sell', 'point', 'stand out', 'differentiator', 'strength'],
  },
  {
    id: 'profile-story',
    title: 'Story',
    path: '/profile/story',
    icon: <BookOpen className="h-4 w-4" />,
    category: 'Profile',
    keywords: ['story', 'journey', 'bio', 'about', 'background', 'narrative'],
  },
  {
    id: 'profile-awards',
    title: 'Achievements',
    path: '/profile/awards-achievements',
    icon: <Award className="h-4 w-4" />,
    category: 'Profile',
    keywords: ['achievements', 'awards', 'recognition', 'track record', 'credentials', 'certifications'],
  },
  {
    id: 'profile-past-performances',
    title: 'Past Performances',
    path: '/profile/past-performances',
    icon: <Calendar className="h-4 w-4" />,
    category: 'Profile',
    keywords: ['past', 'performances', 'event history', 'gigs', 'previous', 'experience'],
  },
  {
    id: 'profile-social-media',
    title: 'Social Media',
    path: '/profile/social-media',
    icon: <Share2 className="h-4 w-4" />,
    category: 'Profile',
    keywords: ['social', 'media', 'instagram', 'connect', 'link', 'follow'],
  },
  {
    id: 'profile-event-essentials',
    title: 'Event Essentials',
    path: '/profile/event-essentials',
    icon: <CalendarCheck className="h-4 w-4" />,
    category: 'Profile',
    keywords: ['event', 'essentials', 'pricing', 'billing', 'bank', 'gst', 'documents', 'event pricing'],
  },
  // Utilities
  {
    id: 'settings',
    title: 'Settings',
    path: '/settings',
    icon: <Settings className="h-4 w-4" />,
    category: 'Utilities',
    keywords: ['settings', 'preferences', 'configuration', 'options', 'setup'],
  },
  {
    id: 'contact-us',
    title: 'Contact Us',
    path: '/contact-us',
    icon: <Smartphone className="h-4 w-4" />,
    category: 'Utilities',
    keywords: ['contact', 'support', 'help', 'email', 'phone', 'reach out'],
  },
  {
    id: 'faq',
    title: 'FAQ',
    path: '/faq',
    icon: <HelpCircle className="h-4 w-4" />,
    category: 'Utilities',
    keywords: ['faq', 'questions', 'answers', 'help', 'guide', 'tutorial', 'express booking faq', 'express booking'],
  },
  {
    id: 'express-booking',
    title: 'Express Booking',
    path: '/jobs',
    icon: <Sparkles className="h-4 w-4" />,
    category: 'Main Navigation',
    keywords: ['express booking', 'express leads', 'high intent clients', 'fast track booking', 'quick booking', 'instant response'],
  },
  {
    id: 'terms',
    title: 'Terms of Service',
    path: '/terms',
    icon: <FileCheck className="h-4 w-4" />,
    category: 'Utilities',
    keywords: ['terms', 'service', 'legal', 'policy', 'tos', 'agreement'],
  },
  // Information (user-home sections)
  {
    id: 'contact-details',
    title: 'Contact Details',
    path: '/user-home',
    icon: <Smartphone className="h-4 w-4" />,
    category: 'Information',
    keywords: ['contact details', 'email', 'phone', 'address', 'reach'],
  },
  {
    id: 'utilities',
    title: 'Utilities',
    path: '/user-home',
    icon: <BarChart3 className="h-4 w-4" />,
    category: 'Information',
    keywords: ['utilities', 'tools', 'features'],
  },
  {
    id: 'website-designs',
    title: 'Preview My Website',
    path: '/user-home',
    icon: <Sparkles className="h-4 w-4" />,
    category: 'Information',
    keywords: ['website', 'designs', 'templates', 'free', 'economy', 'premium', 'elite', 'preview'],
  },
];

// Add FAQ items to search index
const faqSearchResults: SearchResult[] = faqData.map(faq => ({
  id: `faq-${faq.id}`,
  title: faq.content,
  path: `/faq#${faq.id}`,
  icon: <HelpCircle className="h-4 w-4" />,
  category: `FAQ - ${faq.name}`,
  keywords: [
    ...faq.content.toLowerCase().split(' '),
    ...faq.value.toLowerCase().split(' ').slice(0, 10), // First 10 words from answer
    faq.name.toLowerCase(),
  ],
  type: 'faq' as const,
}));

// Combine all search results
const allSearchIndexBase = [...searchIndex, ...faqSearchResults];

function getSearchIndex(): SearchResult[] {
  return allSearchIndexBase;
}

// Debounce hook for performance
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  // const _location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Debounce search query for performance (300ms delay)
  const debouncedQuery = useDebounce(searchQuery.toLowerCase().trim(), 300);

  // Optimized search function with memoization
  const searchResults = useMemo(() => {
    if (!debouncedQuery) return [];

    const query = debouncedQuery;
    const results: SearchResult[] = [];

    for (const item of getSearchIndex()) {
      // Exact title match (highest priority)
      if (item.title.toLowerCase() === query) {
        results.unshift(item);
        continue;
      }

      // Title starts with query
      if (item.title.toLowerCase().startsWith(query)) {
        results.push(item);
        continue;
      }

      // Title contains query
      if (item.title.toLowerCase().includes(query)) {
        results.push(item);
        continue;
      }

      // Keyword match
      const keywordMatch = item.keywords.some(keyword =>
        keyword.toLowerCase().includes(query) || query.includes(keyword.toLowerCase())
      );
      if (keywordMatch) {
        results.push(item);
      }
    }

    // Remove duplicates and limit results
    const uniqueResults = Array.from(
      new Map(results.map(item => [item.id, item])).values()
    ).slice(0, 10);

    return uniqueResults;
  }, [debouncedQuery]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchResults]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === 'Enter' && searchResults[selectedIndex]) {
        e.preventDefault();
        handleResultClick(searchResults[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, searchResults, selectedIndex, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && searchResults.length > 0) {
      const selectedElement = resultsRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, searchResults]);

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
    onClose();
    setSearchQuery('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Search Modal */}
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1.0] }}
            className="fixed top-16 sm:top-20 left-0 right-0 sm:left-1/2 sm:-translate-x-1/2 w-full sm:w-auto sm:max-w-2xl z-50 px-4 sm:px-0"
          >
            <div className="rounded-2xl sm:rounded-2xl shadow-2xl border border-[#3A3A3C]/50 overflow-hidden mx-auto bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)]">
              {/* Search Input */}
              <div className="flex items-center gap-3 p-3 sm:p-4 border-b border-[#3A3A3C]">
                <Search className="h-5 w-5 text-[#8E8E93] flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleInputChange}
                  placeholder="Search pages, features..."
                  className="flex-1 bg-transparent text-white placeholder-[#8E8E93] outline-none text-sm sm:text-base"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="p-1 rounded-lg hover:bg-[#2C2C2E] transition-colors flex-shrink-0"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4 text-[#8E8E93]" />
                  </button>
                )}
              </div>

              {/* Search Results */}
              {debouncedQuery && (
                <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
                  {searchResults.length > 0 ? (
                    <div ref={resultsRef} className="p-1 sm:p-2">
                      {searchResults.map((result, index) => (
                        <motion.button
                          key={result.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.02 }}
                          onClick={() => handleResultClick(result)}
                          className={cn(
                            'w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg transition-colors text-left',
                            'hover:bg-[#2C2C2E] active:bg-[#2C2C2E]',
                            selectedIndex === index && 'bg-[#2C2C2E]'
                          )}
                        >
                          <div className="text-[#8E8E93] flex-shrink-0">
                            {result.icon}
                          </div>
                          <div className="flex-1 min-w-0 flex flex-col justify-center">
                            <div className="text-white text-sm font-medium truncate">
                              {result.title}
                            </div>
                            <div className="text-[#8E8E93] text-xs truncate mt-0.5">
                              {result.category}
                            </div>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 sm:p-8 text-center">
                      <p className="text-[#8E8E93] text-sm">No results found</p>
                      <p className="text-[#8E8E93] text-xs mt-1">Try a different search term</p>
                    </div>
                  )}
                </div>
              )}

              {/* Empty State */}
              {!debouncedQuery && (
                <div className="p-6 sm:p-8 text-center">
                  <Search className="h-10 w-10 sm:h-12 sm:w-12 text-[#8E8E93] mx-auto mb-3 opacity-50" />
                  <p className="text-[#8E8E93] text-sm">Start typing to search</p>
                  <p className="text-[#8E8E93] text-xs mt-1">Search pages, features, and more</p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
